import {
  type HeadlineResponse,
  OrganizationType,
  type OrgCompositeRankingResRelatedRankingResponseRelatedPagedData,
  type StateResponse,
  v1CategoriesHeadlinesDetail2,
  v1OrganizationCompositeRankingsList,
  v1StatesList,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import type { AxiosError } from 'axios';
import { HighSchoolHome } from 'components/Home/HighSchool/HighSchoolHome';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';

export interface HighSchoolHomePageProps {
  latestArticles: HeadlineResponse[] | null;
  states: StateResponse[] | null;
  topRankings: OrgCompositeRankingResRelatedRankingResponseRelatedPagedData | null;
}

interface IHighSchoolHomePage
  extends HighSchoolHomePageProps,
    IGenericPageProps {}

const title = 'High School Sports Scores, Schedules, Rankings and News';
const description =
  'Discover high school sports at On3.com, including football, boys basketball, girls basketball, softball, and baseball. We cover news, schedules, rankings, and scores.';
const canonical = 'https://www.on3.com/high-school/';
const HighSchoolHomePage = (props: IHighSchoolHomePage) => {
  return (
    <PageWrapper
      canonical={canonical}
      {...props}
      description={description}
      title={title}
    >
      <HighSchoolHome {...props} />;
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const highSchoolSiteKey = 402;
  const siteData = await withValidUrl.isValid(ctx, highSchoolSiteKey);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return { notFound: true };
  }

  const { currentSite } = siteData;

  const hsHeadlinesParams = {
    limit: 12,
    category: currentSite?.categoryKey || 0,
  };
  const teamRankParams = {
    orgType: OrganizationType.HighSchool,
    sportKey: 1,
    year: siteData.defaultYears.highSchoolYear || new Date().getFullYear(),
    items: 10,
  };

  try {
    const [latestArticles, topRankings, states] = await Promise.allSettled([
      v1CategoriesHeadlinesDetail2(proxyApi, hsHeadlinesParams),
      v1OrganizationCompositeRankingsList(proxyApi, teamRankParams),
      v1StatesList(proxyApi, { countryKey: 1 }),
    ]);

    return {
      props: {
        pageType: 'home',
        siteData,
        userData: userData || {},
        latestArticles: getFulfilledValue(latestArticles),
        topRankings: getFulfilledValue(topRankings),
        states: getFulfilledValue(states),
      },
    };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.log(err);

    return { props: {} };
  }
};

export default withValidUrl(HighSchoolHomePage);
